import React, { useState, useEffect, useRef } from "react";
import { getMediaDevice, debounce } from "../../helpers";
import { useSwipeable } from "react-swipeable";
import Pager from "./Pager";

interface itemsToDisplay {
  DESKTOPXL: number;
  DESKTOP: number;
  TABLET: number;
  MOBILE: number;
}

interface CarouselProps {
  children: any;
  itemsToDisplay: itemsToDisplay;
  totalItems: number;
  isNavTop?: Boolean;
  hasLetterLabels?: Boolean;
  isPagerFullWidth?: Boolean;
  paginateSingleItem?: Boolean;
  hasPager?: Boolean;
  pagerLabels?: string[];
  pagerPadding?: Boolean;
  pagerPerItem?: Boolean;
  initialPageIndex?: number;
  hasStickyNavigation?: boolean;
  disableScreenResizeAdjustment?: boolean;
  unstickCondition?: () => boolean;
  isDropdownOnMobile?: boolean;
  chevronNext?: JSX.Element;
  chevronPrev?: JSX.Element;
}

export const Carousel = (props: CarouselProps) => {
  const {
    isNavTop,
    hasLetterLabels,
    isPagerFullWidth,
    paginateSingleItem = false,
    itemsToDisplay,
    totalItems,
    pagerLabels,
    hasPager = true,
    pagerPerItem = false,
    pagerPadding = true,
    initialPageIndex = 0,
    hasStickyNavigation,
    disableScreenResizeAdjustment = false,
    unstickCondition,
    isDropdownOnMobile = false,
    chevronNext = null,
    chevronPrev = null
  } = props;

  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [pagerClicked, setPagerClicked] = useState(false);
  const [pagingIndexChange, setPagingIndexChange] = useState();
  const [isSticky, setIsSticky] = useState(false);
  const [transitionType, setTransitionType] = useState<
    "slide-right" | "slide-left"
  >("slide-left");
  let [mode, setMode] = useState<"DESKTOPXL" | "DESKTOP" | "TABLET" | "MOBILE">(
    getMediaDevice()
  );

  const itemsPerPage = itemsToDisplay[mode];

  const canClickPrev = pageIndex > 0;
  const canClickNext = paginateSingleItem
    ? pageIndex < totalItems - itemsToDisplay[mode]
    : totalItems - itemsPerPage * pageIndex > itemsPerPage;

  // paging buttons
  const onClickPrevious = () => {
    // on page if not on the first item
    if (!isDisabled && canClickPrev) {
      setPagerClicked(false);
      setIsUnchanged(false);
      setIsDisabled(true);
      setTransitionType("slide-right");
      setPageIndex(pageIndex - 1);
      // unbind the click for the time it takes to animate
      setTimeout(() => {
        setIsDisabled(false);
      }, 500);
    }
  };

  const onClickNext = () => {
    // only page if there are more items to show
    if (canClickNext && !isDisabled) {
      setPagerClicked(false);
      setIsUnchanged(false);
      setIsDisabled(true);
      setTransitionType("slide-left");
      setPageIndex(pageIndex + 1);
      // unbind the click for the time it takes to animate
      setTimeout(() => {
        setIsDisabled(false);
      }, 500);
    }
  };

  // content
  const childrenWithProps = React.Children.map(props.children, child =>
    React.cloneElement(child, {
      page: pageIndex,
      itemsPerPage,
      transitionType,
      isUnchanged,
      pagerClicked,
      pagingIndexChange
    })
  );

  const handlers = useSwipeable({
    onSwiped: (eventData: any) => {
      if (eventData.dir === "Left") {
        onClickNext();
      } else if (eventData.dir === "Right") {
        onClickPrevious();
      }
    }
  });

  const navRef: any = useRef();

  const handleResize = () => {
    if (navRef && navRef.current !== null) {
      let heightOffset = 0;
      const siteHeader = document.getElementById("site-header") as HTMLElement;
      if (siteHeader) {
        heightOffset = siteHeader.offsetHeight;
      }

      const navTop = navRef.current.getBoundingClientRect().y;
      // if it's less than the height of the nav then make it sticky by adding a class
      // only call this stuff if it doesn't have the clas
      if (navTop <= heightOffset) {
        setIsSticky(true);
      }

      if (unstickCondition && unstickCondition()) {
        setIsSticky(false);
      }
    }
  };

  useEffect(() => {
    if (!disableScreenResizeAdjustment) {
      setPageIndex(pageIndex);
    }
  }, [mode]);

  useEffect(() => {
    if (!disableScreenResizeAdjustment) {
      const func = () => {
        setMode(getMediaDevice());
      };
      func();
      debounce({ func, delay: 125 });
    }
  }, []);

  // check position of carousel-nav-container--mobile
  if (hasStickyNavigation) {
    useEffect(() => {
      window.addEventListener("scroll", handleResize);

      return () => {
        window.removeEventListener("scroll", handleResize);
      };
    }, []);
  }

  return (
    <div className={"carousel-wrapper" + (isNavTop ? " is-nav-top" : "")}>
      <div
        className={`carousel ${isSticky ? "carousel--sticky-nav" : ""}`}
        data-transition-type={transitionType}
        data-pager-change={pagerClicked}
        data-paging-index-change={pagingIndexChange}
      >
        <div className="carousel__items-wrapper">
          <div className="carousel__items " {...handlers}>
            {childrenWithProps}
          </div>
        </div>
      </div>
      <div
        ref={navRef}
        className={`carousel-nav-container${
          getMediaDevice() === "MOBILE" ? "--mobile" : ""
        } ${isPagerFullWidth ? "carousel-nav-container--full-width " : ""}${
          !pagerPadding ? " carousel-nav-container--no-padding" : ""
        }
        ${isSticky ? "carousel-nav-container--sticky" : ""}
        ${isDropdownOnMobile ? "carousel-nav-container--mobile-dropdown" : ""}
        `}
      >
        {totalItems > 1 ? (
          <div className={`${isSticky ? "container" : ""}`}>
            <div className="carousel__nav-btn carousel__nav-btn--prev">
              {canClickPrev ? (
                <button className="btn--chevron-prev" onClick={onClickPrevious}>
                  {chevronPrev}
                </button>
              ) : null}
            </div>
            {hasPager && totalItems > itemsPerPage ? (
              <Pager
                setPagingIndexChange={setPagingIndexChange}
                selectedPager={pageIndex}
                setPageIndex={setPageIndex}
                setTransitionType={setTransitionType}
                pageIndex={pageIndex}
                isDisabled={isDisabled}
                setIsDisabled={setIsDisabled}
                hasLetterLabels={hasLetterLabels}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                pagerLabels={pagerLabels}
                pagerPerItem={pagerPerItem}
                paginateSingleItem={paginateSingleItem}
                setPagerClicked={setPagerClicked}
                setIsUnchanged={setIsUnchanged}
                isDropdownOnMobile={isDropdownOnMobile}
              />
            ) : null}
            <div className="carousel__nav-btn carousel__nav-btn--next">
              {canClickNext ? (
                <button className="btn--chevron-next" onClick={onClickNext}>
                  {chevronNext}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
